// Import the base API configuration from API.ts
import AppApi from './API'
// Import configured axios instance for making HTTP requests
import axiosInstance from './AxiosInstance'
// Import Redux actions for updating map view vehicles and unique vehicle data
import {setGridData, setMapViewVehicle, setUniqueData} from '../MapDataSlice'

function normalizeAvailableVehicle(vehicle: any) {
  const itemID = vehicle?.itemID ?? vehicle?.ItemID
  return {
    ...vehicle,
    itemID,
    registrationNo: vehicle?.registrationNo ?? vehicle?.RegistrationNo ?? '',
    vehicleType: vehicle?.vehicleType ?? vehicle?.VehicleType ?? null,
    baseLocation: vehicle?.baseLocation ?? vehicle?.BaseLocation ?? null,
  }
}

function pruneMapViewVehicle(getState: () => any, uniqueData: {itemID: number}[], dispatch: any) {
  const prev: number[] = getState()?.MapData?.MapViewVehicle || []
  const idSet = new Set(uniqueData.map((v) => v.itemID))
  const next = prev.filter((id) => idSet.has(id))
  dispatch(setMapViewVehicle(next))
}

// Define and export RTK Query endpoints for vehicle assignment functionality
export const AssignVehicleData = AppApi.injectEndpoints({
  endpoints: (builder) => ({
    // Fleet/map list only — no monthly stats
    assignVehicle: builder.query({
      queryFn: async (
        {comapny, requestBody}: {comapny: number; requestBody: any},
        {dispatch, getState}: any
      ) => {
        try {
          const response = await axiosInstance.post(
            `/thiredparty/available-vehicle/${comapny}`,
            requestBody
          )
          const data = response.data
          const rawList =
            data?.gpsAvailableVehicleViewModelList ??
            data?.GPSAvailableVehicleViewModelList ??
            []

          const uniqueData = Array.from(
            new Map(
              (Array.isArray(rawList) ? rawList : [])
                .map(normalizeAvailableVehicle)
                .filter((vehicle: any) => vehicle.itemID)
                .map((vehicle: any) => [vehicle.itemID, vehicle])
            ).values()
          )

          dispatch(setUniqueData(uniqueData))
          pruneMapViewVehicle(getState, uniqueData, dispatch)
          // Stats load separately on Grid tab
          dispatch(setGridData([]))

          return {data}
        } catch (error) {
          return {error: (error as any).response?.data || 'Something went wrong'}
        }
      },
      providesTags: ['assignVehicle'],
    }),

    availableVehicleStatistics: builder.query({
      queryFn: async ({
        company,
        requestBody,
        page = 1,
        itemsPerPage = 10,
        search = '',
        sortBy = 'jobCount',
        sortDir = 'desc',
        timeRange = 'month',
      }: {
        company: number
        requestBody: any
        page?: number
        itemsPerPage?: number
        search?: string
        sortBy?: string
        sortDir?: string
        timeRange?: 'month' | 'all'
      }) => {
        try {
          const params = new URLSearchParams()
          params.set('page', String(page))
          params.set('items_per_page', String(itemsPerPage))
          if (search?.trim()) params.set('search', search.trim())
          if (sortBy) params.set('sortBy', sortBy)
          if (sortDir) params.set('sortDir', sortDir)
          params.set('timeRange', timeRange === 'all' ? 'all' : 'month')

          const response = await axiosInstance.post(
            `/thiredparty/available-vehicle-statistics/${company}?${params.toString()}`,
            requestBody
          )
          const data = response.data
          const items =
            data?.items ?? data?.Items ?? []
          return {
            data: {
              items: Array.isArray(items) ? items : [],
              totalCount: data?.totalCount ?? data?.TotalCount ?? 0,
              page: data?.page ?? data?.Page ?? page,
              itemsPerPage: data?.itemsPerPage ?? data?.ItemsPerPage ?? itemsPerPage,
            },
          }
        } catch (error) {
          return {error: (error as any).response?.data || 'Something went wrong'}
        }
      },
      providesTags: ['availableVehicleStatistics'],
    }),
  }),
})

// Export the generated RTK Query hooks for component usage
export const {useAssignVehicleQuery, useAvailableVehicleStatisticsQuery} = AssignVehicleData
